import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerAndContributorIds } from 'src/components/form';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import RiskForm from 'src/pages/risks/forms/RiskForm';
import type { RiskFormDataFields } from 'src/pages/risks/forms/riskSchema';
import { defaultValues } from 'src/pages/risks/forms/riskSchema';
import { getContributors, getOwners } from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useUpdateRisk } from '@/hooks/mutations/risk';
import { useGetRiskById } from '@/hooks/queries';

const Tab: FC = () => {
  useI18NSummaryHelpContent('risks.help');
  const riskId = useGetGuidParam('riskId');
  const navigate = useNavigate();
  const { data, error, loading } = useGetRiskById({ queryArgs: { riskId } });
  if (error) {
    throw error;
  }
  const risk = data?.risk[0];

  const { hasPermission: canEditRisk, loading: canEditRiskLoading } =
    useHasPermissionQuery('update:risk', risk);
  const { updateRisk } = useUpdateRisk();

  const onSave = async (riskFormData: RiskFormDataFields) => {
    if (!risk) {
      throw new Error('Missing risk');
    }
    await updateRisk({
      Id: risk.Id,
      ...ownerAndContributorIds(riskFormData),
      DepartmentTypeIds:
        riskFormData.departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: riskFormData.tags?.map((t) => t.TagTypeId) || [],
      Tier: riskFormData.Tier,
      Title: riskFormData.Title,
      Description: riskFormData.Description,
      Treatment: riskFormData.Treatment,
      Status: riskFormData.Status,
      CustomAttributeData: riskFormData.CustomAttributeData ?? null,
      ParentRiskId: riskFormData.ParentRiskId ?? null,
      schedule: riskFormData.schedule,
    });
  };
  const onDismiss = () => navigate(-1);

  return (
    <RiskForm
      parentRiskNode={risk?.parentNode}
      latestTestDate={risk?.scheduleState?.LatestDate || undefined}
      onSave={onSave}
      onDismiss={onDismiss}
      values={{
        ...defaultValues,
        ...risk,
        Description: risk?.Description ?? '',
        Tier: risk?.Tier as 1 | 2 | 3,
        Owners: getOwners(risk),
        Contributors: getContributors(risk),
        ancestorContributors: risk?.ancestorContributors ?? [],
        schedule: risk?.schedule ?? defaultValues.schedule,
      }}
      readOnly={!canEditRisk || canEditRiskLoading || loading}
      riskId={risk?.Id}
    />
  );
};

export default Tab;
